import {
  deleteV1CompaniesCompanyIdTreadAgentSkillsId,
  patchV1CompaniesCompanyIdTreadAgentSkillsId,
  postV1CompaniesCompanyIdTreadAgentSkills,
  TreadAgentSkill_Create,
  TreadAgentSkill_Update,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { TreadAgentSkill } from '~hooks/useTreadAgent/models';
import { queryClient } from '~queries/queryClient';
import { TREAD_AGENT_SKILLS_QUERY_KEY } from '~queries/treadAgentQueryKeys';
import connection from '~services/connectionModule';

const invalidateSkillList = (companyId: string) =>
  queryClient.invalidateQueries({
    queryKey: [TREAD_AGENT_SKILLS_QUERY_KEY, companyId],
    exact: true,
  });

export default function useTreadAgentSkills() {
  const createSkill = useCallback(
    async (companyId: string, body: TreadAgentSkill_Create) => {
      try {
        const response = await postV1CompaniesCompanyIdTreadAgentSkills({
          path: { 'company-id': companyId },
          body,
        });
        await invalidateSkillList(companyId);

        return TreadAgentSkill.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.tread_agent.failed_to_create_skill'),
        );
        throw error;
      }
    },
    [],
  );

  const updateSkill = useCallback(
    async (companyId: string, skillId: string, body: TreadAgentSkill_Update) => {
      try {
        const response = await patchV1CompaniesCompanyIdTreadAgentSkillsId({
          path: { 'company-id': companyId, id: skillId },
          body,
        });
        await invalidateSkillList(companyId);

        return TreadAgentSkill.parse(response.data.data);
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        connection.handleRequestError(
          error,
          t('error_messages.tread_agent.failed_to_update_skill'),
        );
        throw error;
      }
    },
    [],
  );

  const deleteSkill = useCallback(async (companyId: string, skillId: string) => {
    try {
      await deleteV1CompaniesCompanyIdTreadAgentSkillsId({
        path: { 'company-id': companyId, id: skillId },
      });
      queryClient.removeQueries({
        queryKey: [TREAD_AGENT_SKILLS_QUERY_KEY, companyId, skillId],
      });
      await invalidateSkillList(companyId);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.tread_agent.failed_to_delete_skill'),
      );
      throw error;
    }
  }, []);

  return { createSkill, deleteSkill, updateSkill };
}
